<script setup lang="ts">
import { PV } from "@/widgets";
import { computed } from "vue";
import { formatRupiah } from "../../common/formats.common";
import { calculateInvoiceNumbers } from "../../common/invoice.common";
import type { InvoiceDiscount, InvoiceItem } from "../../common/types.common";

const props = defineProps<{
    items: InvoiceItem[];
    discounts: InvoiceDiscount[];
    label?: string;
    taxRemittance?: number;
    showTaxRemittance?: boolean;
    marketingProfit?: number;
}>();

const invoiceNumbers = computed(() => calculateInvoiceNumbers(props.items, props.discounts));
const hasWholePrice = computed(() => {
    return props.items.reduce((n, i) => n + (i.wholePrice === true ? 1 : 0), 0) > 0;
});
</script>

<template>
    <div class="flex flex-col gap-2 w-full overflow-x-hidden">
        <div v-if="props.label !== undefined" class="font-semibold text-xl px-2">
            {{ props.label }}
        </div>

        <div class="w-full overflow-x-auto">
            <table class="rns-table-items w-full">
                <thead>
                    <tr>
                        <th></th>
                        <th class="text-start">Nama Produk</th>
                        <th>Qty</th>
                        <th class="text-end">Harga</th>
                        <th class="text-end">Diskon</th>
                        <th class="text-end">Total Harga</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in props.items">
                        <td>{{ index + 1 }}</td>
                        <td>{{ item.name }}</td>
                        <td class="text-center">
                            <PV.Badge severity="contrast" :value="item.quantity" />
                        </td>
                        <td class="text-end">
                            {{ (item.wholePrice === true ? "*" : "") + formatRupiah(item.price) }}
                        </td>
                        <td class="text-end">{{ formatRupiah(item.discount) }}</td>
                        <td class="text-end">{{ formatRupiah(item.total) }}</td>
                    </tr>
                    <tr class="rns-table-total">
                        <td colspan="5">Subtotal</td>
                        <td class="text-end">{{ formatRupiah(invoiceNumbers.sumTotal) }}</td>
                    </tr>
                    <tr v-if="invoiceNumbers.sumDiscount > 0" class="rns-table-total">
                        <td colspan="5">Total Diskon</td>
                        <td class="text-end font-semibold">- {{ formatRupiah(invoiceNumbers.sumDiscount) }}</td>
                    </tr>
                    <tr v-for="discount in props.discounts" class="rns-table-total">
                        <td colspan="5">{{ discount.name }}</td>
                        <td class="text-end font-semibold">- {{ formatRupiah(discount.cost) }}</td>
                    </tr>
                    <tr v-if="invoiceNumbers.taxAddition > 0" class="rns-table-total">
                        <td colspan="5">Total Pajak</td>
                        <td class="text-end">{{ formatRupiah(invoiceNumbers.taxAddition) }}</td>
                    </tr>
                    <tr class="bg-gray-800 text-white">
                        <td class="text-end font-bold" colspan="5">Grand Total</td>
                        <td class="text-end font-bold">{{ formatRupiah(invoiceNumbers.grandTotal) }}</td>
                    </tr>
                    <template v-if="props.marketingProfit !== undefined">
                        <tr class="bg-gray-700 text-white font-medium">
                            <td class="font-bold"><i class="pi pi-wallet"></i></td>
                            <td class="text-end" colspan="2">Pendapatan Marketing</td>
                            <td class="text-end">{{ formatRupiah(props.marketingProfit) }}</td>
                            <td class="text-end">Pendapatan Mitra</td>
                            <td class="text-end">
                                {{ formatRupiah(invoiceNumbers.grandTotal - props.marketingProfit) }}
                            </td>
                        </tr>
                    </template>
                    <tr
                        v-if="props.showTaxRemittance === true && props.taxRemittance !== undefined"
                        class="bg-gray-600 font-semibold text-white"
                    >
                        <td colspan="6">
                            Pajak yang harus dibayarkan mitra sebesar {{ formatRupiah(props.taxRemittance) }}.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="hasWholePrice" class="text-gray-600 font-bold text-sm">* Harga keseluruhan (bukan satuan).</div>
    </div>
</template>

<style scoped>
@reference "@/styles/main.css";

.rns-table-items {
    @apply border border-gray-800;
}

.rns-table-items th,
.rns-table-items td {
    @apply px-4 py-2;
}

.rns-table-items th {
    @apply font-semibold;
}

.rns-table-total td:first-child {
    @apply text-end font-medium;
}

.rns-table-total td {
    @apply bg-gray-200;
}
</style>
